import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Input, Spinner } from '@/components';
import { AudioPreviewButton } from '@/components/admin/AudioPreviewButton';
import { ResourceCardColorPicker } from '@/components/admin/ResourceCardColorPicker';
import type {
  AdminTermSheet,
  TermSheetMatchResponse,
  TermSheetMediaUpload,
} from '@/lib/admin-types';
import { apiJson, getApiErrorMessage } from '@/lib/api';
import { expandAudioUploads } from '@/lib/flashcard-audio-zip';
import { InlineMarkdown } from '@/components/InlineMarkdown';
import { INLINE_MARKDOWN_HINT, stripInlineMarkdown } from '@/lib/inlineMarkdown';
import {
  DEFAULT_TERM_SHEET_CATEGORY,
  normalizeLearningHubCategory,
  TERM_SHEET_CATEGORIES,
} from '@/lib/learning-hub-categories';
import {
  resourceCardColorPayload,
  type ResourceCardColorOption,
} from '@/lib/resource-card-colors';
import { cloudTermSheetResourceKey, termSheetPracticeSearch } from '@/lib/term-sheet-practice';

const AUTOSAVE_INTERVAL_MS = 60_000;

type DraftTerm = {
  localId: string;
  id?: number;
  sort_order: number;
  title: string;
  translation_en: string;
  subtitle_en: string;
  example_en: string;
  used_for_en: string;
  examples_en: string;
  persistency_key?: string;
  sound_adult_cdn_key: string | null;
  sound_adult_url: string | null;
  sound_children_cdn_key: string | null;
  sound_children_url: string | null;
};

type DraftBlock = {
  localId: string;
  id?: number;
  sort_order: number;
  block_type: 'intro' | 'section_header' | 'prose' | 'term_list' | 'divider';
  title_en: string;
  body_en: string;
  terms: DraftTerm[];
};

type UploadedAudio = {
  original_filename: string;
  cdn_key: string;
  url: string;
  voice?: string | null;
};

function newLocalId(): string {
  return `local-${Math.random().toString(36).slice(2, 10)}`;
}

function sheetToDraft(sheet: AdminTermSheet): DraftBlock[] {
  if (!sheet.blocks.length) {
    return [
      {
        localId: newLocalId(),
        sort_order: 0,
        block_type: 'intro',
        title_en: '',
        body_en: '',
        terms: [],
      },
      {
        localId: newLocalId(),
        sort_order: 1,
        block_type: 'term_list',
        title_en: '',
        body_en: '',
        terms: [],
      },
    ];
  }
  return sheet.blocks.map((block, idx) => ({
    localId: `block-${block.id || idx}`,
    id: block.id,
    sort_order: block.sort_order,
    block_type: block.block_type as DraftBlock['block_type'],
    title_en: block.title_translations?.en || '',
    body_en: block.body_translations?.en || '',
    terms: (block.terms || []).map((term, tIdx) => ({
      localId: `term-${term.id || tIdx}`,
      id: term.id,
      sort_order: term.sort_order,
      title: term.title,
      translation_en: term.translations?.en || '',
      subtitle_en: term.subtitle_translations?.en || '',
      example_en: term.example_translations?.en || '',
      used_for_en: term.used_for_translations?.en || '',
      examples_en: term.examples_translations?.en || '',
      persistency_key: term.persistency_key,
      sound_adult_cdn_key: term.sound_adult_cdn_key,
      sound_adult_url: term.sound_url,
      sound_children_cdn_key: term.sound_children_cdn_key,
      sound_children_url: term.sound_children_url,
    })),
  }));
}

function draftToPayload(blocks: DraftBlock[]) {
  return {
    blocks: blocks.map((block, idx) => ({
      id: block.id,
      sort_order: idx,
      block_type: block.block_type,
      title_translations: block.title_en ? { en: block.title_en } : {},
      body_translations: block.body_en ? { en: block.body_en } : {},
      terms:
        block.block_type === 'term_list'
          ? block.terms.map((term, tIdx) => ({
              id: term.id,
              sort_order: tIdx,
              title: term.title,
              translations: term.translation_en ? { en: term.translation_en } : {},
              subtitle_translations: term.subtitle_en ? { en: term.subtitle_en } : {},
              example_translations: term.example_en ? { en: term.example_en } : {},
              used_for_translations: term.used_for_en ? { en: term.used_for_en } : {},
              examples_translations: term.examples_en ? { en: term.examples_en } : {},
              persistency_key: term.persistency_key,
              sound_adult_cdn_key: term.sound_adult_cdn_key,
              sound_children_cdn_key: term.sound_children_cdn_key,
            }))
          : [],
    })),
  };
}

type DraftSnapshotInput = {
  name: string;
  audioVoices: 'dual' | 'single';
  requiresSubscription: boolean;
  flashcardEnabled: boolean;
  category: string;
  backgroundColor: string;
  textColor: string;
  badgeBackground: string;
  blocks: DraftBlock[];
};

function buildDraftSnapshot(input: DraftSnapshotInput): string {
  return JSON.stringify({
    name: input.name.trim(),
    audio_voices: input.audioVoices,
    requires_subscription: input.requiresSubscription,
    flashcard_enabled: input.flashcardEnabled,
    category: input.category,
    background_color: input.backgroundColor,
    text_color: input.textColor,
    badge_background: input.badgeBackground,
    ...draftToPayload(input.blocks),
  });
}

function TermSheetDetailPage() {
  const { id } = Route.useParams();
  const sheetId = Number(id);
  const qc = useQueryClient();

  const { data: sheet, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'term-sheets', sheetId],
    queryFn: () => apiJson<AdminTermSheet>(`/v1/admin/term-sheets/${sheetId}`),
    enabled: Number.isFinite(sheetId),
  });

  const [name, setName] = useState('');
  const [audioVoices, setAudioVoices] = useState<'dual' | 'single'>('dual');
  const [requiresSubscription, setRequiresSubscription] = useState(false);
  const [flashcardEnabled, setFlashcardEnabled] = useState(true);
  const [category, setCategory] = useState(DEFAULT_TERM_SHEET_CATEGORY);
  const [backgroundColor, setBackgroundColor] = useState('');
  const [textColor, setTextColor] = useState('');
  const [badgeBackground, setBadgeBackground] = useState('');
  const [blocks, setBlocks] = useState<DraftBlock[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<'list' | 'flashcards'>('list');
  const [cardIndex, setCardIndex] = useState(0);
  const [uploadedAudio, setUploadedAudio] = useState<UploadedAudio[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [importJson, setImportJson] = useState('');
  const [showRichFields, setShowRichFields] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const hydratedSheetIdRef = useRef<number | null>(null);
  const lastSavedSnapshotRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  const draftStateRef = useRef<DraftSnapshotInput & { busy: string | null }>({
    name: '',
    audioVoices: 'dual',
    requiresSubscription: false,
    flashcardEnabled: true,
    category: DEFAULT_TERM_SHEET_CATEGORY,
    backgroundColor: '',
    textColor: '',
    badgeBackground: '',
    blocks: [],
    busy: null,
  });

  useEffect(() => {
    hydratedSheetIdRef.current = null;
    lastSavedSnapshotRef.current = null;
  }, [sheetId]);

  useEffect(() => {
    if (!sheet) return;
    // Hydrate once per sheet id so document saves/refetches don't wipe in-progress edits.
    if (hydratedSheetIdRef.current === sheet.id) return;
    hydratedSheetIdRef.current = sheet.id;
    setName(sheet.name);
    const voices = (sheet.audio_voices as 'dual' | 'single') || 'dual';
    setAudioVoices(voices);
    setRequiresSubscription(sheet.requires_subscription);
    setFlashcardEnabled(sheet.flashcard_enabled);
    const nextCategory = normalizeLearningHubCategory(sheet.category);
    setCategory(nextCategory);
    setBackgroundColor(sheet.background_color || '');
    setTextColor(sheet.text_color || '');
    setBadgeBackground(sheet.badge_background || '');
    const draft = sheetToDraft(sheet);
    setBlocks(draft);
    setSelectedId(
      draft.find((b) => b.block_type === 'term_list')?.localId ?? draft[0]?.localId ?? null
    );
    lastSavedSnapshotRef.current = buildDraftSnapshot({
      name: sheet.name,
      audioVoices: voices,
      requiresSubscription: sheet.requires_subscription,
      flashcardEnabled: sheet.flashcard_enabled,
      category: nextCategory,
      backgroundColor: sheet.background_color || '',
      textColor: sheet.text_color || '',
      badgeBackground: sheet.badge_background || '',
      blocks: draft,
    });
  }, [sheet]);

  const selected = blocks.find((b) => b.localId === selectedId) || null;
  const flatTerms = useMemo(
    () => blocks.filter((b) => b.block_type === 'term_list').flatMap((b) => b.terms),
    [blocks]
  );
  const dual = audioVoices === 'dual';
  const currentSnapshot = useMemo(
    () =>
      buildDraftSnapshot({
        name,
        audioVoices,
        requiresSubscription,
        flashcardEnabled,
        category,
        backgroundColor,
        textColor,
        badgeBackground,
        blocks,
      }),
    [
      name,
      audioVoices,
      requiresSubscription,
      flashcardEnabled,
      category,
      backgroundColor,
      textColor,
      badgeBackground,
      blocks,
    ]
  );
  const isDirty =
    lastSavedSnapshotRef.current != null && currentSnapshot !== lastSavedSnapshotRef.current;

  draftStateRef.current = {
    name,
    audioVoices,
    requiresSubscription,
    flashcardEnabled,
    category,
    backgroundColor,
    textColor,
    badgeBackground,
    blocks,
    busy,
  };

  function onPickCardColor(option: ResourceCardColorOption) {
    const payload = resourceCardColorPayload(option);
    setBackgroundColor(payload.background_color);
    setTextColor(payload.text_color);
    setBadgeBackground(payload.badge_background);
  }

  const audioUrlByCdnKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of uploadedAudio) {
      if (a.cdn_key && a.url) map.set(a.cdn_key, a.url);
    }
    return map;
  }, [uploadedAudio]);

  function resolveSoundUrl(
    storedUrl: string | null,
    cdnKey: string | null
  ): string | null {
    return storedUrl || (cdnKey ? audioUrlByCdnKey.get(cdnKey) ?? null : null);
  }

  const saveMeta = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiJson<AdminTermSheet>(`/v1/admin/term-sheets/${sheetId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: (saved) => {
      qc.setQueryData(['admin', 'term-sheets', sheetId], saved);
      void qc.invalidateQueries({ queryKey: ['admin', 'term-sheets'], exact: true });
      setMessage('Saved');
    },
    onError: (err) => setMessage(getApiErrorMessage(err)),
  });

  async function saveDraft(reason: 'manual' | 'autosave') {
    const state = draftStateRef.current;
    if (!Number.isFinite(sheetId) || savingRef.current || state.busy) return;
    const snapshot = buildDraftSnapshot(state);
    if (reason === 'autosave' && snapshot === lastSavedSnapshotRef.current) return;

    savingRef.current = true;
    setIsSaving(true);
    if (reason === 'autosave') setMessage('Autosaving…');
    try {
      await apiJson<AdminTermSheet>(`/v1/admin/term-sheets/${sheetId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: state.name.trim(),
          title_translations: { en: state.name.trim() },
          audio_voices: state.audioVoices,
          requires_subscription: state.requiresSubscription,
          flashcard_enabled: state.flashcardEnabled,
          category: state.category,
          background_color: state.backgroundColor,
          text_color: state.textColor,
          badge_background: state.badgeBackground,
        }),
      });
      const saved = await apiJson<AdminTermSheet>(
        `/v1/admin/term-sheets/${sheetId}/document`,
        {
          method: 'PUT',
          body: JSON.stringify(draftToPayload(state.blocks)),
        }
      );
      qc.setQueryData(['admin', 'term-sheets', sheetId], saved);
      void qc.invalidateQueries({ queryKey: ['admin', 'term-sheets'], exact: true });
      lastSavedSnapshotRef.current = snapshot;
      setMessage(reason === 'autosave' ? 'Autosaved' : 'Document saved');
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }

  useEffect(() => {
    if (!Number.isFinite(sheetId)) return;
    const timer = window.setInterval(() => {
      void saveDraft('autosave');
    }, AUTOSAVE_INTERVAL_MS);
    return () => window.clearInterval(timer);
    // Intentionally only rebind when the sheet changes; saveDraft reads latest draft via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId]);

  const importDoc = useMutation({
    mutationFn: (payload: unknown) =>
      apiJson<AdminTermSheet>('/v1/admin/term-sheets/import', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: (created) => {
      setMessage(`Imported draft #${created.id} (${created.slug})`);
      void qc.invalidateQueries({ queryKey: ['admin', 'term-sheets'] });
    },
    onError: (err) => setMessage(getApiErrorMessage(err)),
  });

  async function uploadAudioFiles(fileList: FileList | null) {
    if (!fileList?.length) return;
    setBusy('Uploading audio…');
    try {
      const expanded = await expandAudioUploads(Array.from(fileList));
      const uploaded: UploadedAudio[] = [];
      for (const item of expanded) {
        const form = new FormData();
        form.append('file', item.file);
        form.append('convert_sync', 'true');
        form.append('original_filename', item.relativePath);
        if (item.voice) form.append('voice', item.voice);
        const res = await fetch(`/v1/admin/term-sheets/${sheetId}/audio/upload`, {
          method: 'POST',
          body: form,
          credentials: 'include',
        });
        if (!res.ok) throw new Error(await res.text());
        const data = (await res.json()) as TermSheetMediaUpload;
        uploaded.push({
          original_filename: data.original_filename,
          cdn_key: data.cdn_key,
          url: data.url,
          voice: data.voice ?? item.voice,
        });
      }
      setUploadedAudio((prev) => [...prev, ...uploaded]);
      setMessage(`Uploaded ${uploaded.length} audio file(s)`);
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function matchAudioForSelectedList() {
    if (!selected || selected.block_type !== 'term_list') return;
    setBusy('Matching audio…');
    try {
      const match = await apiJson<TermSheetMatchResponse>(
        `/v1/admin/term-sheets/${sheetId}/match-media`,
        {
          method: 'POST',
          body: JSON.stringify({
            terms: selected.terms.map((t) => ({ title: t.title })),
            audio_filenames: uploadedAudio.map((a) => a.original_filename),
          }),
        }
      );
      const byName = new Map(uploadedAudio.map((a) => [a.original_filename, a]));
      setBlocks((prev) =>
        prev.map((block) => {
          if (block.localId !== selected.localId) return block;
          return {
            ...block,
            terms: block.terms.map((term, idx) => {
              const assignment = match.assignments.find((a) => a.term_index === idx);
              if (!assignment) return term;
              const adult = assignment.sound_adult_filename
                ? byName.get(assignment.sound_adult_filename)
                : null;
              const child = assignment.sound_children_filename
                ? byName.get(assignment.sound_children_filename)
                : null;
              return {
                ...term,
                sound_adult_cdn_key: adult?.cdn_key ?? term.sound_adult_cdn_key,
                sound_adult_url: adult?.url ?? term.sound_adult_url,
                sound_children_cdn_key: child?.cdn_key ?? term.sound_children_cdn_key,
                sound_children_url: child?.url ?? term.sound_children_url,
              };
            }),
          };
        })
      );
      setMessage(
        `Matched audio. Unmatched: ${match.unmatched_audio.length || 0}`
      );
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  function addBlock(type: DraftBlock['block_type']) {
    const block: DraftBlock = {
      localId: newLocalId(),
      sort_order: blocks.length,
      block_type: type,
      title_en: type === 'section_header' ? 'New section' : '',
      body_en: '',
      terms: [],
    };
    setBlocks((prev) => [...prev, block]);
    setSelectedId(block.localId);
  }

  function moveBlock(index: number, delta: -1 | 1) {
    setBlocks((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return next.map((block, sort_order) => ({ ...block, sort_order }));
    });
  }

  function updateSelected(patch: Partial<DraftBlock>) {
    if (!selected) return;
    setBlocks((prev) =>
      prev.map((b) => (b.localId === selected.localId ? { ...b, ...patch } : b))
    );
  }

  function updateTerm(localId: string, patch: Partial<DraftTerm>) {
    if (!selected) return;
    setBlocks((prev) =>
      prev.map((b) =>
        b.localId !== selected.localId
          ? b
          : {
              ...b,
              terms: b.terms.map((t) => (t.localId === localId ? { ...t, ...patch } : t)),
            }
      )
    );
  }

  async function onReplaceTermAudio(
    termLocalId: string,
    voice: 'adult' | 'children',
    fileList: FileList | null
  ) {
    const file = fileList?.[0];
    if (!file) return;
    const voiceLabel =
      voice === 'adult' ? (dual ? 'adult audio' : 'audio') : 'child audio';
    setBusy(`Replacing ${voiceLabel}…`);
    setMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('convert_sync', 'true');
      form.append('original_filename', file.name);
      form.append('voice', voice);
      const data = await apiJson<TermSheetMediaUpload>(
        `/v1/admin/term-sheets/${sheetId}/audio/upload`,
        { method: 'POST', body: form }
      );
      const media: UploadedAudio = {
        original_filename: data.original_filename,
        cdn_key: data.cdn_key,
        url: data.url,
        voice: data.voice ?? voice,
      };
      setUploadedAudio((prev) => [...prev, media]);
      if (voice === 'adult') {
        updateTerm(termLocalId, {
          sound_adult_cdn_key: media.cdn_key,
          sound_adult_url: media.url,
        });
      } else {
        updateTerm(termLocalId, {
          sound_children_cdn_key: media.cdn_key,
          sound_children_url: media.url,
        });
      }
      setMessage(
        `${voice === 'adult' ? (dual ? 'Adult audio' : 'Audio') : 'Child audio'} updated — autosaves every minute`
      );
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    setBusy('Publishing…');
    try {
      await saveDraft('manual');
      await saveMeta.mutateAsync({ status: 'live' });
      setMessage('Published live');
    } catch (err) {
      setMessage(getApiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (isError || !sheet) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error)?.message || 'Not found'}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/term-sheets" className="text-sm text-primary hover:underline">
            ← Term sheets
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">{sheet.name}</h1>
            <Badge variant={sheet.status === 'live' ? 'success' : 'warning'}>{sheet.status}</Badge>
            <Badge>{sheet.mode}</Badge>
            <Badge>audio: {audioVoices}</Badge>
          </div>
          <p className="mt-1 font-mono text-xs text-gray-500">{sheet.slug}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              disabled={!!busy || isSaving}
              onClick={() => void saveDraft('manual')}
            >
              {isSaving ? 'Saving…' : 'Save draft'}
            </Button>
            {sheet.status === 'live' ? (
              <Button
                variant="secondary"
                onClick={() => saveMeta.mutate({ status: 'draft' })}
              >
                Unpublish
              </Button>
            ) : (
              <Button disabled={!!busy || isSaving} onClick={() => void publish()}>
                Publish
              </Button>
            )}
          </div>
          <p className="text-[11px] text-gray-500" data-autosave-interval-ms={AUTOSAVE_INTERVAL_MS}>
            {isDirty ? 'Unsaved changes · autosaves every minute' : 'Autosaves every minute'}
          </p>
        </div>
      </div>

      {(message || busy) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {busy || message}
        </div>
      )}

      {sheet.validation_errors?.length ? (
        <Card className="border-amber-200 bg-amber-50">
          <h2 className="text-sm font-medium text-amber-900">Publish blockers</h2>
          <ul className="mt-2 list-disc pl-5 text-sm text-amber-900">
            {sheet.validation_errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card className="border-emerald-200 bg-emerald-50 text-sm text-emerald-900">
          Ready to publish — validation passed on last saved document.
        </Card>
      )}

      <Card className="space-y-2">
        <h2 className="text-sm font-medium text-gray-900">Practice games</h2>
        <p className="text-sm text-gray-600">
          Add practice sentences for this sheet (resource key{' '}
          <code className="rounded bg-gray-100 px-1">
            {cloudTermSheetResourceKey(sheet.slug)}
          </code>
          ). Create drafts, then publish them to make them live in the app.
        </p>
        <Link
          to="/admin/term-sheets/practice"
          search={termSheetPracticeSearch({
            resourceKey: cloudTermSheetResourceKey(sheet.slug),
            title: sheet.name,
            kind: 'cloud',
            sheetId: sheet.id,
          })}
          className="inline-block text-sm font-medium text-primary hover:underline"
        >
          Add & manage practice →
        </Link>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Card className="space-y-3">
            <h2 className="text-sm font-medium">Sheet meta</h2>
            <div className="flex flex-wrap gap-3">
              <label className="block text-xs text-gray-600">
                Name
                <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="block text-xs text-gray-600">
                Audio voices
                <select
                  className="mt-1 block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  value={audioVoices}
                  onChange={(e) => setAudioVoices(e.target.value as 'dual' | 'single')}
                >
                  <option value="dual">Dual</option>
                  <option value="single">Single</option>
                </select>
              </label>
              <label className="block text-xs text-gray-600">
                Learning Hub category
                <select
                  className="mt-1 block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                  value={category}
                  onChange={(e) =>
                    setCategory(normalizeLearningHubCategory(e.target.value))
                  }
                  disabled={isSaving || !!busy}
                >
                  {TERM_SHEET_CATEGORIES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 pt-5 text-sm">
                <input
                  type="checkbox"
                  checked={requiresSubscription}
                  onChange={(e) => setRequiresSubscription(e.target.checked)}
                />
                Subscribers only
              </label>
              <label className="flex items-center gap-2 pt-5 text-sm">
                <input
                  type="checkbox"
                  checked={flashcardEnabled}
                  onChange={(e) => setFlashcardEnabled(e.target.checked)}
                />
                Flashcard toggle
              </label>
            </div>
            <ResourceCardColorPicker
              value={backgroundColor}
              onChange={onPickCardColor}
              disabled={isSaving || !!busy}
            />
          </Card>

          <div className="grid gap-4 md:grid-cols-[240px_1fr]">
            <Card className="space-y-2 p-3">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Page structure
                </h2>
                <div className="relative">
                  <select
                    className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs"
                    defaultValue=""
                    onChange={(e) => {
                      const v = e.target.value as DraftBlock['block_type'] | '';
                      if (v) addBlock(v);
                      e.target.value = '';
                    }}
                  >
                    <option value="" disabled>
                      + Add block
                    </option>
                    <option value="intro">Intro</option>
                    <option value="section_header">Section header</option>
                    <option value="prose">Prose</option>
                    <option value="term_list">Term list</option>
                    <option value="divider">Divider</option>
                  </select>
                </div>
              </div>
              <ul className="space-y-1">
                {blocks.map((block, index) => (
                  <li key={block.localId} className="space-y-0.5">
                    <button
                      type="button"
                      className={`w-full rounded-md border px-2 py-2 text-left text-sm ${
                        selectedId === block.localId
                          ? 'border-primary bg-primary/5'
                          : 'border-transparent hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedId(block.localId)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-600">
                          {block.block_type.replace('_', ' ')}
                        </span>
                        {block.block_type === 'term_list' ? (
                          <span className="text-[10px] text-gray-500">
                            {block.terms.length} terms
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-gray-700">
                        {block.block_type === 'section_header'
                          ? stripInlineMarkdown(block.title_en) || 'Untitled'
                          : block.block_type === 'term_list'
                            ? block.terms[0]?.title || 'Empty list'
                            : stripInlineMarkdown(block.body_en) || 'Empty'}
                      </div>
                    </button>
                    <div className="flex items-center gap-1 px-1">
                      <button
                        type="button"
                        className="rounded px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
                        disabled={index === 0}
                        aria-label="Move block up"
                        onClick={() => moveBlock(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="rounded px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
                        disabled={index === blocks.length - 1}
                        aria-label="Move block down"
                        onClick={() => moveBlock(index, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="ml-auto text-[11px] text-gray-400 hover:text-red-600"
                        onClick={() => {
                          setBlocks((prev) => prev.filter((_, i) => i !== index));
                          if (selectedId === block.localId) setSelectedId(null);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="space-y-3">
              {!selected ? (
                <p className="text-sm text-gray-500">Select a block to edit.</p>
              ) : (
                <>
                  <h2 className="text-sm font-medium capitalize">
                    {selected.block_type.replace('_', ' ')} block
                  </h2>
                  {selected.block_type === 'section_header' ? (
                    <div className="space-y-1">
                      <Input
                        value={selected.title_en}
                        onChange={(e) => updateSelected({ title_en: e.target.value })}
                        placeholder="Section title (EN)"
                      />
                      <p className="text-[11px] text-gray-500">{INLINE_MARKDOWN_HINT}</p>
                    </div>
                  ) : null}
                  {selected.block_type === 'intro' || selected.block_type === 'prose' ? (
                    <div className="space-y-1">
                      <textarea
                        className="min-h-[120px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        value={selected.body_en}
                        onChange={(e) => updateSelected({ body_en: e.target.value })}
                        placeholder="Body text (EN) — e.g. Use **puas** before the verb."
                      />
                      <p className="text-[11px] text-gray-500">{INLINE_MARKDOWN_HINT}</p>
                    </div>
                  ) : null}
                  {selected.block_type === 'divider' ? (
                    <p className="text-sm text-gray-500">Visual spacer — no content.</p>
                  ) : null}
                  {selected.block_type === 'term_list' ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        <label className="inline-flex cursor-pointer items-center rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium hover:bg-gray-50">
                          Upload audio
                          <input
                            type="file"
                            className="hidden"
                            multiple
                            accept=".wav,.mp3,.m4a,.amr,.zip"
                            onChange={(e) => void uploadAudioFiles(e.target.files)}
                          />
                        </label>
                        <Button
                          variant="secondary"
                          className="!h-8 !text-xs"
                          disabled={!uploadedAudio.length}
                          onClick={() => void matchAudioForSelectedList()}
                        >
                          Match filenames
                        </Button>
                        <Button
                          className="!h-8 !text-xs"
                          onClick={() =>
                            updateSelected({
                              terms: [
                                ...selected.terms,
                                {
                                  localId: newLocalId(),
                                  sort_order: selected.terms.length,
                                  title: '',
                                  translation_en: '',
                                  subtitle_en: '',
                                  example_en: '',
                                  used_for_en: '',
                                  examples_en: '',
                                  sound_adult_cdn_key: null,
                                  sound_adult_url: null,
                                  sound_children_cdn_key: null,
                                  sound_children_url: null,
                                },
                              ],
                            })
                          }
                        >
                          + Term
                        </Button>
                        <label className="flex items-center gap-1 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={showRichFields}
                            onChange={(e) => setShowRichFields(e.target.checked)}
                          />
                          Rich fields
                        </label>
                        <span className="text-[11px] text-gray-500">{INLINE_MARKDOWN_HINT}</span>
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-gray-50 text-xs text-gray-500">
                            <tr>
                              <th className="px-2 py-2">#</th>
                              <th className="px-2 py-2">Hmong</th>
                              <th className="px-2 py-2">
                                {showRichFields ? 'English (optional)' : 'English'}
                              </th>
                              {showRichFields ? (
                                <>
                                  <th className="px-2 py-2">Subtitle</th>
                                  <th className="px-2 py-2">Example</th>
                                  <th className="px-2 py-2">Used for</th>
                                </>
                              ) : null}
                              <th className="px-2 py-2">{dual ? 'Adult' : 'Audio'}</th>
                              {dual ? <th className="px-2 py-2">Child</th> : null}
                              <th className="px-2 py-2" />
                            </tr>
                          </thead>
                          <tbody>
                            {selected.terms.map((term, idx) => (
                              <tr key={term.localId} className="border-t border-gray-100">
                                <td className="px-2 py-2 text-xs text-gray-400">{idx + 1}</td>
                                <td className="px-2 py-2">
                                  <input
                                    className="h-8 w-28 rounded border border-gray-300 px-2 text-sm"
                                    value={term.title}
                                    onChange={(e) =>
                                      updateTerm(term.localId, { title: e.target.value })
                                    }
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <input
                                    className="h-8 w-32 rounded border border-gray-300 px-2 text-sm"
                                    value={term.translation_en}
                                    placeholder={
                                      showRichFields ? 'Optional' : undefined
                                    }
                                    onChange={(e) =>
                                      updateTerm(term.localId, {
                                        translation_en: e.target.value,
                                      })
                                    }
                                  />
                                </td>
                                {showRichFields ? (
                                  <>
                                    <td className="px-2 py-2">
                                      <input
                                        className="h-8 w-28 rounded border border-gray-300 px-2 text-sm"
                                        value={term.subtitle_en}
                                        onChange={(e) =>
                                          updateTerm(term.localId, {
                                            subtitle_en: e.target.value,
                                          })
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <input
                                        className="h-8 w-28 rounded border border-gray-300 px-2 text-sm"
                                        value={term.example_en}
                                        onChange={(e) =>
                                          updateTerm(term.localId, {
                                            example_en: e.target.value,
                                          })
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <input
                                        className="h-8 w-28 rounded border border-gray-300 px-2 text-sm"
                                        value={term.used_for_en}
                                        onChange={(e) =>
                                          updateTerm(term.localId, {
                                            used_for_en: e.target.value,
                                          })
                                        }
                                      />
                                    </td>
                                  </>
                                ) : null}
                                <td className="px-2 py-2">
                                  <div className="flex flex-col items-start gap-1">
                                    <AudioPreviewButton
                                      url={resolveSoundUrl(
                                        term.sound_adult_url,
                                        term.sound_adult_cdn_key
                                      )}
                                      cdnKey={term.sound_adult_cdn_key}
                                      label={`${term.title || 'term'} ${dual ? 'adult audio' : 'audio'}`}
                                    />
                                    <label
                                      className={`text-xs text-primary hover:underline ${
                                        busy
                                          ? 'pointer-events-none opacity-50'
                                          : 'cursor-pointer'
                                      }`}
                                    >
                                      {term.sound_adult_cdn_key ? 'Replace' : 'Upload'}
                                      <input
                                        type="file"
                                        accept="audio/*,.wav,.m4a,.amr,.mp3"
                                        className="hidden"
                                        disabled={!!busy}
                                        onChange={(e) => {
                                          void onReplaceTermAudio(
                                            term.localId,
                                            'adult',
                                            e.target.files
                                          );
                                          e.target.value = '';
                                        }}
                                      />
                                    </label>
                                  </div>
                                </td>
                                {dual ? (
                                  <td className="px-2 py-2">
                                    <div className="flex flex-col items-start gap-1">
                                      <AudioPreviewButton
                                        url={resolveSoundUrl(
                                          term.sound_children_url,
                                          term.sound_children_cdn_key
                                        )}
                                        cdnKey={term.sound_children_cdn_key}
                                        label={`${term.title || 'term'} child audio`}
                                      />
                                      <label
                                        className={`text-xs text-primary hover:underline ${
                                          busy
                                            ? 'pointer-events-none opacity-50'
                                            : 'cursor-pointer'
                                        }`}
                                      >
                                        {term.sound_children_cdn_key ? 'Replace' : 'Upload'}
                                        <input
                                          type="file"
                                          accept="audio/*,.wav,.m4a,.amr,.mp3"
                                          className="hidden"
                                          disabled={!!busy}
                                          onChange={(e) => {
                                            void onReplaceTermAudio(
                                              term.localId,
                                              'children',
                                              e.target.files
                                            );
                                            e.target.value = '';
                                          }}
                                        />
                                      </label>
                                    </div>
                                  </td>
                                ) : null}
                                <td className="px-2 py-2">
                                  <button
                                    type="button"
                                    className="text-xs text-gray-400 hover:text-red-600"
                                    onClick={() =>
                                      updateSelected({
                                        terms: selected.terms.filter(
                                          (t) => t.localId !== term.localId
                                        ),
                                      })
                                    }
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {uploadedAudio.length ? (
                        <p className="text-xs text-gray-500">
                          {uploadedAudio.length} audio file(s) in session buffer for matching.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </Card>
          </div>

          <Card className="space-y-2">
            <h2 className="text-sm font-medium">Import document JSON (Phase 3)</h2>
            <p className="text-xs text-gray-500">
              Paste a full document (name, mode, blocks…) to create a new draft via
              <code className="mx-1">POST /v1/admin/term-sheets/import</code>.
            </p>
            <textarea
              className="min-h-[100px] w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs"
              value={importJson}
              onChange={(e) => setImportJson(e.target.value)}
              placeholder='{"name":"…","mode":"hmonglus","blocks":[…]}'
            />
            <Button
              variant="secondary"
              disabled={!importJson.trim() || importDoc.isPending}
              onClick={() => {
                try {
                  const parsed = JSON.parse(importJson);
                  importDoc.mutate(parsed);
                } catch {
                  setMessage('Invalid JSON');
                }
              }}
            >
              Import as draft
            </Button>
          </Card>
        </div>

        <aside className="space-y-3">
          <Card className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium">Mobile preview</h2>
              <div className="flex rounded-md border border-gray-200 p-0.5 text-xs">
                <button
                  type="button"
                  className={`rounded px-2 py-1 ${previewMode === 'list' ? 'bg-gray-900 text-white' : ''}`}
                  onClick={() => setPreviewMode('list')}
                >
                  List
                </button>
                <button
                  type="button"
                  className={`rounded px-2 py-1 ${previewMode === 'flashcards' ? 'bg-gray-900 text-white' : ''}`}
                  onClick={() => setPreviewMode('flashcards')}
                >
                  Cards
                </button>
              </div>
            </div>
            <div className="mx-auto w-[260px] overflow-hidden rounded-[1.75rem] border-[7px] border-slate-900 bg-[#faf8f5]">
              <div className="px-3 pb-4 pt-3">
                <div className="mb-2 text-center text-sm font-extrabold text-slate-900">
                  {name || sheet.name}
                </div>
                {previewMode === 'list' ? (
                  <div className="max-h-[480px] space-y-2 overflow-y-auto">
                    {blocks.map((block) => (
                      <div key={block.localId}>
                        {block.block_type === 'intro' || block.block_type === 'prose' ? (
                          <InlineMarkdown
                            as="p"
                            text={block.body_en}
                            className="mb-2 whitespace-pre-wrap text-[12px] leading-relaxed text-slate-700"
                          />
                        ) : null}
                        {block.block_type === 'section_header' ? (
                          <InlineMarkdown
                            as="h3"
                            text={block.title_en}
                            className="mb-1 mt-3 text-sm font-extrabold text-slate-900"
                          />
                        ) : null}
                        {block.block_type === 'term_list'
                          ? block.terms.map((term) => (
                              <div
                                key={term.localId}
                                className="mb-2 rounded-2xl px-3 py-2"
                                style={{ background: '#C4EBC0' }}
                              >
                                <div className="text-[14px] font-extrabold text-slate-900">
                                  {term.title || '—'}
                                </div>
                                <InlineMarkdown
                                  text={term.translation_en}
                                  className="text-xs font-semibold italic text-slate-600"
                                />
                                {term.subtitle_en ? (
                                  <InlineMarkdown
                                    text={term.subtitle_en}
                                    className="mt-1 text-[11px] font-bold text-slate-600"
                                  />
                                ) : null}
                                {term.used_for_en ? (
                                  <div className="mt-1 text-[11px] text-slate-600">
                                    <span className="font-bold">used for:</span>{' '}
                                    <InlineMarkdown text={term.used_for_en} />
                                  </div>
                                ) : null}
                                {term.example_en ? (
                                  <div className="text-[11px] text-slate-600">
                                    <span className="font-bold">example:</span>{' '}
                                    <InlineMarkdown text={term.example_en} />
                                  </div>
                                ) : null}
                              </div>
                            ))
                          : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex h-[420px] flex-col">
                    <div className="flex flex-1 items-center justify-center">
                      <div className="flex h-56 w-full flex-col items-center justify-center rounded-3xl bg-[#B8E0F0] px-3 text-center">
                        <div className="text-2xl font-extrabold text-slate-900">
                          {flatTerms[cardIndex]?.title || '—'}
                        </div>
                        <InlineMarkdown
                          text={flatTerms[cardIndex]?.translation_en || ''}
                          className="mt-2 text-sm font-bold text-slate-700"
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <button
                        type="button"
                        className="rounded border px-2 py-1"
                        onClick={() => setCardIndex((i) => Math.max(0, i - 1))}
                      >
                        Prev
                      </button>
                      <span>
                        {flatTerms.length ? `${cardIndex + 1} / ${flatTerms.length}` : '0'}
                      </span>
                      <button
                        type="button"
                        className="rounded border px-2 py-1"
                        onClick={() =>
                          setCardIndex((i) => Math.min(flatTerms.length - 1, i + 1))
                        }
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/term-sheets/$id')({
  component: TermSheetDetailPage,
});
